import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import logoWhite from "../assets/logo-white.png";
import logoDark from "../assets/logo-dark.png";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "./BookingDialog";
import { CONTACT } from "../data/mock";

const Navbar = () => {
  const { t, lang, toggleLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openBooking } = useBooking();

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/activites", label: t.nav.activities },
    { to: "/a-propos", label: t.nav.about },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent py-5"
          : "bg-[#f7f5ef]/95 backdrop-blur-md shadow-[0_4px_30px_rgba(28,34,30,0.06)] py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center shrink-0">
          <img
            src={transparent ? logoWhite : logoDark}
            alt="AGADIR VIBES"
            className="h-12 md:h-14 w-auto object-contain transition-all duration-500"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`relative text-[13px] tracking-[0.12em] uppercase font-medium transition-colors duration-300 ${
                  transparent ? "text-white/90 hover:text-white" : "text-[#2b332d] hover:text-[#7c8f74]"
                } ${active ? "after:w-full" : "after:w-0"} after:absolute after:-bottom-1.5 after:left-0 after:h-[1.5px] after:bg-[#7c8f74] after:transition-all after:duration-300`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          <button
            onClick={toggleLang}
            className={`hidden sm:flex items-center gap-1 text-xs tracking-[0.15em] font-semibold transition-colors ${
              transparent ? "text-white/80 hover:text-white" : "text-[#2b332d] hover:text-[#7c8f74]"
            }`}
            aria-label="Switch language"
          >
            <span className={lang === "fr" ? "opacity-100" : "opacity-40"}>FR</span>
            <span className="opacity-40">/</span>
            <span className={lang === "en" ? "opacity-100" : "opacity-40"}>EN</span>
          </button>

          <button
            onClick={() => openBooking()}
            className="hidden md:inline-flex items-center gap-2 bg-[#7c8f74] hover:bg-[#697c62] text-white text-[13px] tracking-[0.1em] uppercase font-medium px-6 py-3 rounded-full transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
          >
            {t.nav.book}
          </button>

          <button
            className={`lg:hidden ${transparent ? "text-white" : "text-[#2b332d]"}`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-400 bg-[#f7f5ef] ${
          open ? "max-h-[26rem] border-t border-[#e3e0d6]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="py-3 text-[15px] tracking-wide text-[#2b332d] border-b border-[#eae7dd]"
            >
              {l.label}
            </Link>
          ))}
          <div className="flex items-center justify-between pt-5">
            <button onClick={toggleLang} className="text-sm tracking-[0.15em] font-semibold text-[#2b332d]">
              {lang === "fr" ? "EN ↔" : "FR ↔"}
            </button>
            <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-2 text-sm text-[#7c8f74]">
              <Phone size={16} /> {CONTACT.phone}
            </a>
          </div>
          <button
            onClick={() => { setOpen(false); openBooking(); }}
            className="mt-4 bg-[#7c8f74] text-white text-sm tracking-[0.1em] uppercase font-medium px-6 py-3.5 rounded-full"
          >
            {t.nav.book}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
